import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const STATS = [
  { target: 90, key: 0 },
  { target: 2,   key: 2 },
  { target: 40,  key: 3 },
];

function useCountUp(target: number, duration = 1600, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

interface StatItemProps {
  statKey: number;
  target: number;
  started: boolean;
  delay: string;
}

function StatItem({ statKey, target, started, delay }: StatItemProps) {
  const { t } = useTranslation();
  const count = useCountUp(target, 1600, started);
  return (
    <div className={`anim-count-up ${delay}`} style={{ opacity: started ? undefined : 0 }}>
      <div className="text-4xl xl:text-5xl font-bold text-[#C8953A]">
        {started ? count : 0}{t(`hero.stat${statKey}.sfx`)}
      </div>
      <div className="text-white/45 text-sm mt-2 tracking-wide">{t(`hero.stat${statKey}.lbl`)}</div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [headline, setHeadline] = useState(0);
  const [hlVisible, setHlVisible] = useState(true);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
      setTimeout(() => setStatsVisible(true), 800);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setHlVisible(false);
      setTimeout(() => {
        setHeadline((p) => (p + 1) % 3);
        setHlVisible(true);
      }, 380);
    }, 3200);
    return () => clearInterval(iv);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-[#0C0C0C]">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://storage.readdy-site.link/project_files/ad63308d-79d1-4d1c-8d5c-c7f5e0438d81/359e0d95-e6d0-4255-ae88-2b317b05d97d_Screenshot_2.jpg?v=8e97b805a41d9ee0a6bca1e9b18b8c13)",
        }}
      />
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        {/* Badge */}
        <div className={`inline-flex items-center gap-2.5 bg-white/8 backdrop-blur-sm border border-white/15 rounded-full px-5 py-2.5 mb-10 ${mounted ? "anim-fade-up" : "opacity-0"}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#C8953A] animate-pulse" />
          <span className="text-white/75 text-sm font-medium tracking-widest uppercase">{t("hero.badge")}</span>
        </div>

        {/* Headline */}
        <h1 className={`text-5xl xl:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6 ${mounted ? "anim-fade-up anim-d-100" : "opacity-0"}`}>
          {t("hero.prefix")}
          <br />
          <span
            className="text-[#C8953A] inline-block transition-all duration-300"
            style={{
              opacity: hlVisible ? 1 : 0,
              transform: hlVisible ? "translateY(0)" : "translateY(-12px)",
              minWidth: "8ch",
            }}
          >
            {t(`hero.h${headline}`)}
          </span>
        </h1>

        {/* Sub */}
        <p className={`text-white/55 text-lg xl:text-xl leading-relaxed max-w-xl mb-12 ${mounted ? "anim-fade-up anim-d-200" : "opacity-0"}`}>
          {t("hero.sub")}
        </p>

        {/* CTAs */}
        <div className={`flex flex-wrap gap-4 mb-20 ${mounted ? "anim-fade-up anim-d-300" : "opacity-0"}`}>
          <button
            onClick={() => scrollTo("services")}
            className="whitespace-nowrap bg-[#C8953A] hover:bg-[#a97b30] text-[#0C0C0C] font-semibold px-9 py-4 rounded-full text-base transition-all duration-200 cursor-pointer"
          >
            {t("hero.cta1")}
          </button>
          <button
            onClick={() => scrollTo("contact")}
            className="whitespace-nowrap border border-white/25 hover:border-[#C8953A] text-white/80 hover:text-[#C8953A] font-medium px-9 py-4 rounded-full text-base transition-all duration-200 cursor-pointer"
          >
            {t("hero.cta2")}
          </button>
        </div>

        {/* Stats — 3 items after removing Client Satisfaction */}
        <div className={`grid grid-cols-3 gap-6 xl:gap-8 pt-8 border-t border-white/10 max-w-lg ${mounted ? "anim-fade-up anim-d-400" : "opacity-0"}`}>
          {STATS.map((stat, i) => (
            <StatItem
              key={stat.key}
              statKey={stat.key}
              target={stat.target}
              started={statsVisible}
              delay={`anim-d-${(i + 1) * 100}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-[0.25em] uppercase">{t("hero.scroll")}</span>
        <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-[#C8953A] rounded-full" />
        </div>
      </div>
    </section>
  );
}
